import PDS from '../../../services/dashboards/PerformanceDashboard.service';
import { CategoryLevelItem } from '../../../interfaces/dashboards/PerformanceDashboard.interface';
import BasicTable from '../../../tailwindComponents/tables/basic-table.component';
import React from 'react';

interface ClassPerformanceProps {
    categoryLevelData: CategoryLevelItem[],
    epochMonth: number,
    compareEpochMonth: number,
    compareDateString: string,
}

interface ClassPerformanceItem {
    className: string;
    category: string;
    salesChange: number;
    freshness: number;
    efficiency: number;
    insight: string;
    recommendation: string;
}

/**
 * Returns insight given sales change, freshness, and efficiency
 * 
 * @param item ClassPerformanceItem
 * @returns ClassPerformanceItem with updated insight and recommendation
 */
function getInsightFromItem(item: ClassPerformanceItem): ClassPerformanceItem {
    const { salesChange, freshness, efficiency } = item;

    if (salesChange > 0 && freshness > 0 && efficiency > 0) {
        item.insight = "Fresh Inflow is driving Sales growth";
        item.recommendation = "Focus on opportunities to build market share";
    } else if (salesChange > 0 && freshness <= 0 && efficiency > 0) {
        item.insight = "Older Inventory is driving Sales growth";
        item.recommendation = "Focus on following Inflow Plan to prevent Sales from stalling";
    } else if (salesChange > 0 && freshness > 0 && efficiency <= 0) {
        item.insight = "Fresh Inflow is starting to drive Sales";
        item.recommendation = "Focus on achieving Sales to improve efficiency";
    } else if (salesChange > 0 && freshness <= 0 && efficiency <= 0) {
        item.insight = "Older Inventory is driving Sales growth";
        item.recommendation = "Review how Markdowns are driving Sales, and replace with fresh Inventory";
    } else if (salesChange <= 0 && freshness > 0 && efficiency > 0) {
        item.insight = "Inventory is turning faster";
        item.recommendation = "Focus on areas where S/S ratios could be out of balance";
    } else if (salesChange <= 0 && freshness > 0 && efficiency <= 0) {
        item.insight = "Inventory may be turning too slow and tying up cash";
        item.recommendation = "Focus on promoting Sales, and watch cost of purchases";
    } else if (salesChange <= 0 && freshness <= 0 && efficiency > 0) {
        item.insight = "Lack of fresh Inventory is driving down Sales";
        item.recommendation = "Focus on chasing fresh Inventory if not too late in season";
    } else if (salesChange <= 0 && freshness <= 0 && efficiency <= 0) {
        item.insight = "Lack of fresh Inventory is driving down Sales";
        item.recommendation = "Distress older Inventory and rebuild assortments with fresh Inventory";
    }
    return item;
}

function SalesCustomFormatting(value: number | string): JSX.Element {
    // strip the % sign if it exists
    if (typeof value === 'string') {
        value = value.replace('%', '');
        value = parseFloat(value);
    }
    if (typeof value === 'number') {
        if (value > 0) return <span className="text-emerald-500">{`${PDS.percentageFormat(value)}`}</span>;
        else if (value < 0) return <span className="text-red-500">{`${PDS.percentageFormat(value)}`}</span>;
    }
    return <span>{value}</span>;
}

function NameCustomFormatting(value: string | number): JSX.Element {
    return <span className="font-bold">{value}</span>
}

const ClassPerformance: React.FC<ClassPerformanceProps> = ({ categoryLevelData, epochMonth, compareEpochMonth, compareDateString }) => {
    const [limitItems, setLimitItems] = React.useState(5);
    const [items, setItems] = React.useState<ClassPerformanceItem[]>([]);

    const thisMonthCategoryLevelData = React.useMemo(
        () => categoryLevelData.filter(item => item.epochMonthRM === epochMonth),
        [categoryLevelData, epochMonth]
    );
    
    const compareMonthCategoryLevelData = React.useMemo(
        () => categoryLevelData.filter(item => item.epochMonthRM === compareEpochMonth),
        [categoryLevelData, compareEpochMonth]
    );
    
    React.useEffect(() => {
        // Build the data for each item
        let _items: ClassPerformanceItem[] = thisMonthCategoryLevelData
            .map((item) => {
                const compareItem = compareMonthCategoryLevelData.find(compareItem => compareItem.className === item.className);
    
                if (!compareItem) {
                    return null; // Return null instead of undefined for clarity
                }
    
                const salesChange = compareItem.salesRm ? (item.salesRm - compareItem.salesRm) / Math.abs(compareItem.salesRm) : 0;
    
                const freshness = compareItem.inflowRetailRm && compareItem.inventoryBomRm
                    ? (PDS.calculateFreshness(item.inflowRetailRm, item.inventoryBomRm) -
                    PDS.calculateFreshness(compareItem.inflowRetailRm, compareItem.inventoryBomRm)) /
                    Math.abs(PDS.calculateFreshness(compareItem.inflowRetailRm, compareItem.inventoryBomRm))
                    : 0;
    
                const efficiency = compareItem.salesRm && compareItem.inventoryBomRm && compareItem.inflowRetailRm
                    ? (PDS.calculateEfficiency({
                        salesSumLastMonth: item.salesRm,
                        inventory: item.inventoryBomRm,
                        inflow: item.inflowRetailRm
                    }) -
                    PDS.calculateEfficiency({
                        salesSumLastMonth: compareItem.salesRm,
                        inventory: compareItem.inventoryBomRm,
                        inflow: compareItem.inflowRetailRm
                    })) /
                    Math.abs(PDS.calculateEfficiency({
                        salesSumLastMonth: compareItem.salesRm,
                        inventory: compareItem.inventoryBomRm,
                        inflow: compareItem.inflowRetailRm
                    }))
                    : 0;
    
                return getInsightFromItem({
                    className: item.className,
                    category: item.category,
                    salesChange,
                    freshness,
                    efficiency,
                    insight: "",
                    recommendation: ""
                });
            })
            .filter((item): item is ClassPerformanceItem => item !== null)
            .sort((a, b) => b.salesChange - a.salesChange); // Sort by sales change in descending order
    
        if (limitItems !== 999) {
            _items = _items.slice(0, limitItems);
        }
        setItems(_items);
    }, [limitItems, thisMonthCategoryLevelData, compareMonthCategoryLevelData]);

    function ClassPerformanceContainer ({ children }: { children: React.ReactNode }) {
        return (
            <div className="flex flex-col shadow-xl p-5 rounded-xl border border-gray-300 bg-white">
                <div className="flex flex-row justify-between items-left mb-4">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight">Class Performance</h1>
                        <h3 className="text-lg tracking-tight">View performance metrics accross merchandise classes as changes from {compareDateString}</h3>
                    </div>
                    <div className="flex flex-row items-center">
                        <select
                            id="limitItems"
                            value={limitItems}
                            onChange={(e) => setLimitItems(Number(e.target.value))}
                            className="border border-gray-300 rounded p-2"
                        >
                            <option value={999}>All items</option>
                            <option value={5}>Top 5 items</option>
                            <option value={10}>Top 10 items</option>
                        </select>
                    </div>
                </div>
                {children}
            </div>
        )
    } 

    if (items.length === 0) {
        return <ClassPerformanceContainer>No data available</ClassPerformanceContainer>;
    }

    return (
        <ClassPerformanceContainer>
            <div className="mt-4 max-h-[700px] overflow-y-auto">
                <BasicTable
                    columns={[{name: 'Class', sortable: true, customFormatting: NameCustomFormatting}, 
                        {name: 'Sales', sortable: true, customFormatting: SalesCustomFormatting},
                        'Freshness', 'Efficiency', 'Insight', 'Recommendation'
                    ]}
                    data={items.map((item) => [
                        `${item.className}`,
                        item.salesChange,
                        `${(item.freshness * 100).toFixed(0)}%`,
                        `${(item.efficiency * 100).toFixed(0)}%`,
                        item.insight,
                        item.recommendation,
                    ])}
                    defaultSortColumn={'Sales Change'}
                    defaultSortDirection="desc"
                />
            </div>
        </ClassPerformanceContainer>
    );
};

export default ClassPerformance;